import { useLayoutEffect, useMemo, useRef } from "react";
import { easeOutCubic, formatParsed, motionAllowed, parseFormatted } from "../lib/countup";

/**
 * Número formatado que "conta" até o valor ao aparecer e ao mudar (de onde estava até o novo). O texto final é sempre
 * o recebido; durante a animação só o nó de texto muda (sem renderizar a cada quadro). Sem movimento permitido, ou
 * texto que não é quantidade, mostra direto.
 */
export function AnimatedValue({ text, durationMs = 700 }: { text: string; durationMs?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const last = useRef<number | null>(null);
  const parsed = useMemo(() => parseFormatted(text), [text]);

  useLayoutEffect(() => {
    const node = ref.current?.firstChild;
    if (!node || node.nodeType !== Node.TEXT_NODE) return;
    const from = last.current ?? 0;
    if (!parsed || !motionAllowed() || from === parsed.value) {
      node.nodeValue = text;
      last.current = parsed?.value ?? null;
      return;
    }
    const to = parsed.value;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const v = from + (to - from) * easeOutCubic(t);
      node.nodeValue = t < 1 ? formatParsed(parsed, v) : text;
      last.current = v;
      if (t < 1) frame = window.requestAnimationFrame(tick);
    };
    node.nodeValue = formatParsed(parsed, from);
    frame = window.requestAnimationFrame(tick);
    // Interrompida (valor novo no meio da contagem), a próxima parte de onde parou (last.current).
    return () => {
      window.cancelAnimationFrame(frame);
      node.nodeValue = text;
    };
  }, [text, parsed, durationMs]);

  return <span ref={ref}>{text}</span>;
}
